import type { EntriesQueries, Entry, EntrySkeletonType } from 'contentful';
import { createConfiguredClientBySpace, type SPACE_NAME } from '../utils';
import { remember } from '@tuic/cache';
import { createContentTypeCacheTag, createSpaceCacheTag } from '../utils/cache';
import { onLocal } from '@tuic/lib/src/config';
import { logger } from '@tuic/logger';
import type {
  BrandNameProcessingConfig,
  ContentTypeConfig,
} from '../utils/brand-name-processor';
import {
  processEntries,
  processEntriesWithConfig,
} from '../utils/brand-name-processor';

export type EntryFromSkeletonWithAllLocales<
  Skeleton extends EntrySkeletonType,
> = Entry<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS' | 'WITH_ALL_LOCALES'>;

type BaseQueryAllLocales = {
  content_type: string;
};

type SelectQueryAllLocales = {
  select?: string[];
};

type QueryAllLocales<Skeleton extends EntrySkeletonType> = BaseQueryAllLocales &
  EntriesQueries<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS' | 'WITH_ALL_LOCALES'> &
  SelectQueryAllLocales;

type SingleEntryQueryAllLocales<Skeleton extends EntrySkeletonType> =
  QueryAllLocales<Skeleton> & {
    limit?: undefined | 1;
  };

type GetEntriesAllLocalesArgs<Skeleton extends EntrySkeletonType> = {
  space: SPACE_NAME;
  query: QueryAllLocales<Skeleton>;
  preview?: boolean;
  brandNameProcessing?: BrandNameProcessingConfig | ContentTypeConfig<Skeleton>;
};

type GetEntryAllLocaleArgs<Skeleton extends EntrySkeletonType> = {
  space: SPACE_NAME;
  query: SingleEntryQueryAllLocales<Skeleton>;
  preview?: boolean;
  brandNameProcessing?: BrandNameProcessingConfig | ContentTypeConfig<Skeleton>;
};

/**
 * Fetches entries from given space using given query.
 */
export const getEntriesWithAllLocales = async <
  Skeleton extends EntrySkeletonType,
>({
  space,
  query,
  preview,
  brandNameProcessing,
}: GetEntriesAllLocalesArgs<Skeleton>): Promise<
  EntryFromSkeletonWithAllLocales<Skeleton>[] | []
> => {
  try {
    let modifiedQuery = query;

    // Only modify the query if brandNameProcessing is enabled AND select exists
    if (brandNameProcessing && query.select) {
      modifiedQuery = { ...query }; // copy to avoid caching issues

      // Ensure select is an array and check if it already includes sys.contentType
      const selectArray = Array.isArray(modifiedQuery.select)
        ? [...modifiedQuery.select]
        : [];

      // Only add sys.contentType if not already included
      if (!selectArray.includes('sys.contentType')) {
        modifiedQuery.select = [...selectArray, 'sys.contentType'];
      }
    }

    // Fetch entries with the original or modified query
    const collection = await createConfiguredClientBySpace({
      space,
      preview,
    }).withoutUnresolvableLinks.withAllLocales.getEntries<Skeleton>(
      modifiedQuery,
    );

    // Process entries if brandNameProcessing is enabled
    if (!brandNameProcessing) {
      return collection.items;
    }

    // Use the typed version if it's a ContentTypeConfig
    if (typeof brandNameProcessing === 'object') {
      // TypeScript can't statically determine if this is a ContentTypeConfig<Skeleton>
      // but we maintain type safety through the processEntriesWithConfig function
      return processEntriesWithConfig<
        Skeleton,
        EntryFromSkeletonWithAllLocales<Skeleton>
      >(collection.items, brandNameProcessing as ContentTypeConfig<Skeleton>);
    }

    // Default case - use the standard processEntries function
    return processEntries(collection.items, brandNameProcessing);
  } catch (e) {
    logger.error(
      `[getEntries]: Contentful request failed. Query ${JSON.stringify(query)}`,
    );
    logger.error(e);
  }

  return [];
};

/**
 * Fetches the first entry from given space using given query.
 */
export const getEntryWithAllLocales = async <
  Skeleton extends EntrySkeletonType,
>({
  space,
  query,
  preview,
  brandNameProcessing,
}: GetEntryAllLocaleArgs<Skeleton>): Promise<EntryFromSkeletonWithAllLocales<Skeleton> | null> => {
  try {
    const entries = await getEntriesWithAllLocales<Skeleton>({
      space,
      query: {
        ...query,
        limit: 1,
      },
      preview,
      brandNameProcessing,
    });

    if (entries.length === 0) {
      return null;
    }

    return entries[0];
  } catch (e) {
    logger.error(
      `[getEntry]: Contentful request failed. Query ${JSON.stringify(query)}`,
    );
    logger.error(e);
  }

  return null;
};

export const cachedGetEntryWithAllLocales = async <
  Skeleton extends EntrySkeletonType,
>(
  arg: GetEntryAllLocaleArgs<Skeleton>,
): Promise<EntryFromSkeletonWithAllLocales<Skeleton> | null> => {
  if (arg.preview === true) {
    return getEntryWithAllLocales(arg);
  }

  let key = createQueryKey(arg.space, arg.query, arg.brandNameProcessing);

  if (!key) {
    logger.error(
      `Could not stringify query to key. This request will not be cached. You need to fix this before going to prod. Query: ${arg.query}`,
    );
    return getEntryWithAllLocales(arg);
  }

  return remember(key, () => getEntryWithAllLocales(arg), {
    tags: [
      createSpaceCacheTag(arg.space),
      createContentTypeCacheTag(arg.query.content_type),
    ],
  });
};

export const cachedGetEntriesWithAllLocales = async <
  Skeleton extends EntrySkeletonType,
>(
  arg: GetEntriesAllLocalesArgs<Skeleton>,
): Promise<EntryFromSkeletonWithAllLocales<Skeleton>[] | []> => {
  if (arg.preview === true) {
    return getEntriesWithAllLocales(arg);
  }

  let key = createQueryKey(arg.space, arg.query, arg.brandNameProcessing);

  if (!key) {
    logger.error(
      `Could not stringify query to key. This request will not be cached. You need to fix this before going to prod. Query: ${arg.query}`,
    );
    return getEntriesWithAllLocales(arg);
  }

  return remember(key, () => getEntriesWithAllLocales(arg), {
    tags: [
      createSpaceCacheTag(arg.space),
      createContentTypeCacheTag(arg.query.content_type),
    ],
  });
};

const createQueryKey = (
  space: SPACE_NAME,
  query: BaseQueryAllLocales,
  brandNameConfig?: BrandNameProcessingConfig,
): string | undefined => {
  try {
    let orderedQuery = (Object.keys(query) as unknown as (keyof typeof query)[])
      .sort()
      .reduce<Record<string, any>>((obj, key) => {
        obj[key] = query[key];
        return obj;
      }, {});

    let queryKey = JSON.stringify(orderedQuery);

    let configSuffix = '';

    if (brandNameConfig === true) {
      configSuffix = ':bnp';
    } else if (typeof brandNameConfig === 'object') {
      configSuffix = `:bnp:${JSON.stringify(brandNameConfig)}`;
    }

    return `q:${space}:alc${configSuffix}:${queryKey}`;
  } catch (error) {
    const message = `Could not stringify query to cache key. This request will not be cached. You need to fix this before going to prod. Query: ${JSON.stringify(query) ?? query.toString()}`;

    if (onLocal()) {
      throw new Error(message);
    }

    logger.error(message);
  }

  return undefined;
};
